import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TrendingUp, Users, CheckCircle2, Star, Building2, Plus, UserPlus, Loader2, ClipboardCheck, Calendar, Clock, CreditCard, Upload, Eye, FileText, LayoutDashboard, Settings as SettingsIcon, MapPin, Pencil, Trash2, Link, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar as CalendarUI } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from '../../hooks/use-toast';
import { designationOptions, skillLevels, bloodGroups } from '../../mock';
import api from '../../services/api';
import { format } from 'date-fns';
import { useCalendarHolidays } from '../../hooks/useHolidays';
import ProjectDetailModal from '../../components/ProjectDetailModal';
import QuotationPreparationModal from '../../components/QuotationPreparationModal';
import { StageName, SiteVisitStatus, QuotationStatus, QuotationType, PaymentStatus } from '../../constants/stageConstants';

const EnhancedAdminDashboard = () => {
  const navigate = useNavigate();
  const { holidayDates, isHoliday, getHolidayName, onMonthChange: onHolidayMonthChange } = useCalendarHolidays();

  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    totalSqftThisMonth: 0,
    activeOEMs: 0,
    totalProjects: 0,
    completedProjects: 0,
    activeProjects: 0
  });
  const [oems, setOems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [recentlyCompleted, setRecentlyCompleted] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [siteVisitRequests, setSiteVisitRequests] = useState([]);

  // Tab-specific loading states
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [oemsLoading, setOemsLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);

  // Track which tabs have been loaded
  const [tabsLoaded, setTabsLoaded] = useState({
    projects: false,
    oems: false,
    teams: false,
    supervisors: false
  });

  // Create project dialog
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [cities, setCities] = useState([]);
  const [entities, setEntities] = useState([]);
  const [newProject, setNewProject] = useState({
    oemId: '',
    clientName: '',
    clientPhone: '',
    siteAddress: '',
    cityId: '',
    sqft: '',
    preferredStartDate: format(new Date(), 'yyyy-MM-dd'),
    preferredCompletionDate: '',
    selectedEntities: [] // [{ entityId, quantity }]
  });

  const [activeTab, setActiveTab] = useState('projects');
  const [ratingsDialogOpen, setRatingsDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembersDialogOpen, setTeamMembersDialogOpen] = useState(false);
  const [selectedOEM, setSelectedOEM] = useState(null);
  const [oemProjectsDialogOpen, setOemProjectsDialogOpen] = useState(false);
  const [projectsFilter, setProjectsFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage] = useState(10);
  const [oemsCurrentPage, setOemsCurrentPage] = useState(1);
  const [oemsPerPage] = useState(10);
  const [supervisorsCurrentPage, setSupervisorsCurrentPage] = useState(1);
  const [supervisorsPerPage] = useState(10);

  // Add team/member dialogs
  const [addTeamDialogOpen, setAddTeamDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [viewMembersDialogOpen, setViewMembersDialogOpen] = useState(false);
  const [selectedTeamForMembers, setSelectedTeamForMembers] = useState(null);
  const [currentTeamMembers, setCurrentTeamMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  // Project detail dialog
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);
  const [selectedProjectDetail, setSelectedProjectDetail] = useState(null);
  const [loadingProjectId, setLoadingProjectId] = useState(null);

  // SV Scheduled popup
  const [svScheduledDialogOpen, setSvScheduledDialogOpen] = useState(false);
  const [svScheduledType, setSvScheduledType] = useState('SV1');
  const [svScheduledProjects, setSvScheduledProjects] = useState([]);

  // Schedule SV1 confirmation dialog
  const [scheduleSv1DialogOpen, setScheduleSv1DialogOpen] = useState(false);
  const [scheduleSv1Project, setScheduleSv1Project] = useState(null);
  const [scheduleSv1Data, setScheduleSv1Data] = useState(null);
  const [scheduleSv1Loading, setScheduleSv1Loading] = useState(false);
  const [scheduleSv1Submitting, setScheduleSv1Submitting] = useState(false);

  // Schedule SV2 confirmation dialog
  const [scheduleSv2ConfirmOpen, setScheduleSv2ConfirmOpen] = useState(false);
  const [scheduleSv2Project, setScheduleSv2Project] = useState(null);
  const [scheduleSv2Data, setScheduleSv2Data] = useState(null);
  const [scheduleSv2Loading, setScheduleSv2Loading] = useState(false);
  const [scheduleSv2Submitting, setScheduleSv2Submitting] = useState(false);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Supervisor assignment dialog
  const [assignSupervisorDialogOpen, setAssignSupervisorDialogOpen] = useState(false);
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState(null);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [siteVisit1Date, setSiteVisit1Date] = useState('');

  // SV2 proposal dialog
  const [scheduleSv2DialogOpen, setScheduleSv2DialogOpen] = useState(false);
  const [sv2Date, setSv2Date] = useState(null);
  const [sv2CalendarMonth, setSv2CalendarMonth] = useState(new Date());
  const [sv2MonthAvailability, setSv2MonthAvailability] = useState({ availableDates: [], unavailableDates: [] });
  const [sv2Slots, setSv2Slots] = useState([]);
  const [sv2SelectedSlotId, setSv2SelectedSlotId] = useState('');
  const [sv2Supervisors, setSv2Supervisors] = useState([]);
  const [sv2SelectedSupervisorId, setSv2SelectedSupervisorId] = useState('');
  const [sv2LoadingSlots, setSv2LoadingSlots] = useState(false);
  const [sv2LoadingSupervisors, setSv2LoadingSupervisors] = useState(false);
  const [sv2Submitting, setSv2Submitting] = useState(false);
  const [schedulingInstallation, setSchedulingInstallation] = useState(false);

  // Quotation preparation dialog
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [selectedProjectForQuotation, setSelectedProjectForQuotation] = useState(null);

  // Team assignment dialog
  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [installationScheduledDate, setInstallationScheduledDate] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('5');
  const [projectDetailForSchedule, setProjectDetailForSchedule] = useState(null);
  const [unitNames, setUnitNames] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Payment link dialog
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkProject, setPaymentLinkProject] = useState(null);
  const [paymentLinkForm, setPaymentLinkForm] = useState({ amount: '', description: '', expiryHours: 72 });
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);

  // New team form
  const [newTeam, setNewTeam] = useState({
    name: '',
    skillLevel: 'intermediate',
    location: ''
  });

  // New member form (simplified - just userId and teamId)
  const [newMember, setNewMember] = useState({
    userId: '',
    teamId: ''
  });

  // Edit team dialog
  const [editTeamDialogOpen, setEditTeamDialogOpen] = useState(false);
  const [editTeamTab, setEditTeamTab] = useState('details');
  const [editTeamMembers, setEditTeamMembers] = useState([]);
  const [editTeamAvailableUsers, setEditTeamAvailableUsers] = useState([]);
  const [editTeamNewMemberId, setEditTeamNewMemberId] = useState('');
  const [editTeamData, setEditTeamData] = useState({
    id: '',
    name: '',
    skillLevel: '',
    location: ''
  });

  // Delete confirmation dialog
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);

  // Project closure dialog
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureProject, setClosureProject] = useState(null);
  const [closureDetails, setClosureDetails] = useState(null);
  const [closureLoading, setClosureLoading] = useState(false);
  const [closureNotes, setClosureNotes] = useState('');
  const [closureSubmitting, setClosureSubmitting] = useState(false);
  const [closureItemPhotos, setClosureItemPhotos] = useState({});
  const [closurePhotosExpanded, setClosurePhotosExpanded] = useState({});
  const [closureEnlargedPhoto, setClosureEnlargedPhoto] = useState(null);

  // Calculate estimated days when dates change
  const projectEstimatedDays = React.useMemo(() => {
    if (newProject.preferredStartDate && newProject.preferredCompletionDate) {
      const start = new Date(newProject.preferredStartDate);
      const end = new Date(newProject.preferredCompletionDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    }
    return 0;
  }, [newProject.preferredStartDate, newProject.preferredCompletionDate]);

  // Toggle entity selection
  const toggleEntity = (entityId) => {
    setNewProject(prev => {
      const existing = prev.selectedEntities.find(e => e.entityId === entityId);
      if (existing) {
        // Remove entity
        return {
          ...prev,
          selectedEntities: prev.selectedEntities.filter(e => e.entityId !== entityId)
        };
      } else {
        // Add entity with default quantity 1
        return {
          ...prev,
          selectedEntities: [...prev.selectedEntities, { entityId, quantity: 1 }]
        };
      }
    });
  };

  // Update entity quantity
  const updateEntityQuantity = (entityId, quantity) => {
    setNewProject(prev => ({
      ...prev,
      selectedEntities: prev.selectedEntities.map(e =>
        e.entityId === entityId ? { ...e, quantity: parseInt(quantity) || 1 } : e
      )
    }));
  };

  // Fetch all data on mount
  // Initial load: Only KPIs and essential dropdown data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Lazy load tab data when tab changes
  useEffect(() => {
    if (!tabsLoaded[activeTab]) {
      loadTabData(activeTab);
    }
  }, [activeTab]);

  // Load only essential data on initial mount
  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Load only KPIs, cities (for dropdowns), and entities (for create project)
      const [kpisData, citiesData] = await Promise.all([
        api.getDashboardKpis().catch(() => null),
        api.getLocations(1, 1000).catch(() => ({ items: [] }))
      ]);

      console.log('KPIs API Response:', kpisData);
      console.log('Cities API Response:', citiesData);

      // Set KPIs
      if (kpisData) {
        setKpis({
          totalSqftThisMonth: kpisData.TotalSqftThisMonth || kpisData.totalSqftThisMonth || 0,
          activeOEMs: kpisData.ActiveOEMs || kpisData.activeOEMs || 0,
          totalProjects: kpisData.TotalProjects || kpisData.totalProjects || 0,
          completedProjects: kpisData.CompletedProjects || kpisData.completedProjects || 0,
          activeProjects: kpisData.ActiveProjects || kpisData.activeProjects || 0
        });
      }

      // Transform cities
      const citiesArray = Array.isArray(citiesData) ? citiesData : (citiesData.Locations || citiesData.locations || citiesData.Items || citiesData.items || citiesData.data || []);
      setCities(citiesArray.map(c => ({
        id: c.Id || c.id,
        name: `${c.CityName || c.cityName}, ${c.StateName || c.stateName}`,
        cityName: c.CityName || c.cityName,
        stateName: c.StateName || c.stateName
      })));

      // Load entities for create project form
      await fetchEntities();

      // Load the active tab's data
      await loadTabData(activeTab);

    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data for a specific tab
  const loadTabData = async (tab) => {
    // Skip if already loaded
    if (tabsLoaded[tab]) return;

    switch (tab) {
      case 'projects':
        await fetchProjectsData();
        break;
      case 'oems':
        await fetchOemsData();
        break;
      case 'teams':
        await fetchTeamsData();
        break;
      case 'supervisors':
        await fetchSupervisorsData();
        break;
      default:
        break;
    }

    setTabsLoaded(prev => ({ ...prev, [tab]: true }));
  };

  // Fetch projects data
  const fetchProjectsData = async () => {
    try {
      setProjectsLoading(true);
      console.log('Loading projects data...');

      const projectsData = await api.getProjects().catch(() => ({ items: [], totalCount: 0 }));
      console.log('Projects API Response:', projectsData);

      const projectsList = projectsData.Items || projectsData.items || projectsData.Projects || projectsData.projects || projectsData || [];

      // Backend now includes draftBaseWorkQuotationId per project — no more N+1 quotation fetches
      const projectsWithQuotations = projectsList.map(p => {
        const draftId = p.draftBaseWorkQuotationId || p.DraftBaseWorkQuotationId || null;
        // Keep `draftQuotation` shape for backward compat with downstream UI
        return { ...p, draftQuotation: draftId ? { id: draftId } : null };
      });

      const transformedProjects = projectsWithQuotations.map(p => {
        const baseStageName = p.StageName || p.stageName || p.stage_name || p.status;
        const sv1 = p.siteVisitSV1 || p.SiteVisitSV1;
        const sv2 = p.siteVisitSV2 || p.SiteVisitSV2;

        let displayStatus = baseStageName;
        if (sv1?.status === 'InProgress') {
          displayStatus = 'SV1 In Progress';
        } else if (sv2?.status === 'InProgress') {
          displayStatus = 'SV2 In Progress';
        }

        return {
          id: p.Id || p.id,
          oemId: p.OemId || p.oemId || p.oem_id,
          oemName: p.OemName || p.oemName || p.oem_name,
          clientName: p.ClientName || p.clientName || p.client_name,
          location: p.CityName || p.cityName || p.location,
          sqft: p.Sqft || p.sqft,
          type: p.TypeName || p.typeName || p.type_name || p.type,
          status: displayStatus,
          baseStatus: baseStageName,
          stageKey: baseStageName?.toLowerCase().replace(/ /g, '_'),
          rating: p.Rating || p.rating,
          supervisorId: p.SupervisorId || p.supervisorId || p.supervisor_id,
          supervisorName: p.SupervisorName || p.supervisorName || p.supervisor_name,
          siteVisitSV1: sv1,
          siteVisitSV2: sv2,
          siteVisit1Date: p.site_visit_1_date,
          siteVisit1Completed: p.site_visit_1_completed,
          siteVisit2Date: p.site_visit_2_date,
          siteVisit2Completed: p.site_visit_2_completed,
          paymentStatus: p.payment_status,
          originalQuote: p.original_quote,
          revisedQuote: p.revised_quote,
          oemApprovalStatus: p.oem_approval_status,
          draftQuotation: p.draftQuotation || null,
          hardwareListUploaded: p.HardwareListUploaded ?? p.hardwareListUploaded ?? false
        };
      });

      setProjects(transformedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load projects',
        variant: 'destructive'
      });
    } finally {
      setProjectsLoading(false);
    }
  };

  // Fetch OEMs data
  const fetchOemsData = async () => {
    try {
      setOemsLoading(true);
      console.log('Loading OEMs data...');

      const [oemsData, projectsData] = await Promise.all([
        api.getOems(1, 100).catch(() => ({ items: [] })),
        projects.length > 0 ? Promise.resolve({ items: projects }) : api.getProjects().catch(() => ({ items: [] }))
      ]);

      console.log('OEMs API Response:', oemsData);

      const oemsList = Array.isArray(oemsData) ? oemsData : (oemsData.Items || oemsData.items || oemsData.data || []);
      const projectsList = projectsData.Items || projectsData.items || projectsData.Projects || projectsData.projects || projects || [];

      // Calculate project counts for each OEM
      setOems(oemsList.map(o => {
        const oemId = o.Id || o.id;
        const oemProjects = projectsList.filter(p => (p.oemId || p.OemId) === oemId);
        const activeProjects = oemProjects.filter(p => {
          const status = p.status || p.Status;
          return status !== StageName.ProjectCompleted && status !== StageName.Completed && status !== StageName.Delivered && status !== StageName.Cancelled;
        });

        return {
          id: oemId,
          name: o.CompanyName || o.companyName || o.ContactPerson || o.contactPerson,
          email: o.Email || o.email,
          phone: o.Phone || o.phone || '',
          totalProjects: oemProjects.length,
          activeProjects: activeProjects.length
        };
      }));
    } catch (error) {
      console.error('Error fetching OEMs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load OEMs',
        variant: 'destructive'
      });
    } finally {
      setOemsLoading(false);
    }
  };

  // Fetch teams data
  const fetchTeamsData = async () => {
    try {
      setTeamsLoading(true);
      console.log('Loading teams data...');

      const teamsData = await api.getTeams().catch(() => ({ teams: [] }));
      console.log('Teams API Response:', teamsData);

      const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
      setTeams(teamsList);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to load teams',
        variant: 'destructive'
      });
    } finally {
      setTeamsLoading(false);
    }
  };

  // Fetch supervisors data
  const fetchSupervisorsData = async () => {
    try {
      setSupervisorsLoading(true);
      console.log('Loading supervisors data...');

      const [supervisorsData, projectsData] = await Promise.all([
        api.getSupervisors().catch(() => ({ items: [] })),
        projects.length > 0 ? Promise.resolve({ items: projects }) : api.getProjects().catch(() => ({ items: [] }))
      ]);

      console.log('Supervisors API Response:', supervisorsData);

      const supervisorsList = Array.isArray(supervisorsData) ? supervisorsData : (supervisorsData.Users || supervisorsData.users || supervisorsData.Items || supervisorsData.items || supervisorsData.data || []);
      const projectsList = projectsData.Items || projectsData.items || projectsData.Projects || projectsData.projects || projects || [];

      setSupervisors(supervisorsList.map(s => {
        const supervisorId = s.Id || s.id;
        const supervisorProjects = projectsList.filter(p => (p.supervisorId || p.SupervisorId) === supervisorId);
        const activeProjects = supervisorProjects.filter(p => {
          const status = p.status || p.Status;
          return status !== StageName.ProjectCompleted && status !== StageName.Completed && status !== StageName.Delivered && status !== StageName.Cancelled;
        });

        // Count projects by stage
        const sv1Scheduled = supervisorProjects.filter(p =>
          (p.status === StageName.Sv1Scheduled || p.siteVisitSV1?.status === SiteVisitStatus.Scheduled)
        ).length;
        const sv1InProgress = supervisorProjects.filter(p =>
          p.siteVisitSV1?.status === SiteVisitStatus.InProgress
        ).length;
        const sv1Completed = supervisorProjects.filter(p =>
          (p.status === StageName.Sv1Completed || p.siteVisitSV1?.status === SiteVisitStatus.Completed)
        ).length;
        const sv2Scheduled = supervisorProjects.filter(p =>
          (p.status === StageName.Sv2Scheduled || p.siteVisitSV2?.status === SiteVisitStatus.Scheduled)
        ).length;
        const sv2InProgress = supervisorProjects.filter(p =>
          p.siteVisitSV2?.status === SiteVisitStatus.InProgress
        ).length;
        const sv2Completed = supervisorProjects.filter(p =>
          (p.status === StageName.Sv2Completed || p.siteVisitSV2?.status === SiteVisitStatus.Completed)
        ).length;

        const sv1ScheduledProjects = supervisorProjects.filter(p =>
          (p.status === StageName.Sv1Scheduled || p.siteVisitSV1?.status === SiteVisitStatus.Scheduled)
        ).map(p => ({
          id: p.Id || p.id,
          clientName: p.ClientName || p.clientName || p.client_name,
          location: p.CityName || p.cityName || p.location,
          scheduledDate: p.siteVisitSV1?.scheduledDate || p.siteVisitSV1?.ScheduledDate
        }));
        const sv2ScheduledProjects = supervisorProjects.filter(p =>
          (p.status === StageName.Sv2Scheduled || p.siteVisitSV2?.status === SiteVisitStatus.Scheduled)
        ).map(p => ({
          id: p.Id || p.id,
          clientName: p.ClientName || p.clientName || p.client_name,
          location: p.CityName || p.cityName || p.location,
          scheduledDate: p.siteVisitSV2?.scheduledDate || p.siteVisitSV2?.ScheduledDate
        }));

        return {
          id: supervisorId,
          name: s.Name || s.name,
          email: s.Email || s.email,
          phone: s.Phone || s.phone || '',
          totalProjects: activeProjects.length,
          activeProjects: activeProjects.length,
          sv1Scheduled,
          sv1InProgress,
          sv1Completed,
          sv2Scheduled,
          sv2InProgress,
          sv2Completed,
          sv1ScheduledProjects,
          sv2ScheduledProjects
        };
      }));
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      toast({
        title: 'Error',
        description: 'Failed to load supervisors',
        variant: 'destructive'
      });
    } finally {
      setSupervisorsLoading(false);
    }
  };

  const fetchAllData = async () => {
    // Legacy function - replaced by lazy loading
    // Keeping for backward compatibility with refresh functions
    await Promise.all([
      fetchProjectsData(),
      fetchOemsData(),
      fetchTeamsData(),
      fetchSupervisorsData()
    ]);
    setTabsLoaded({
      projects: true,
      oems: true,
      teams: true,
      supervisors: true
    });
  };

  // Fetch entities for project creation
  const fetchEntities = async () => {
    try {
      const response = await api.getEntities(1, 100);
      const entitiesList = response.items || response.Items || [];
      setEntities(entitiesList);
    } catch (error) {
      console.error('Error fetching entities:', error);
    }
  };

  // Handle assigning supervisor to project
  const handleAssignSupervisor = async () => {
    if (!selectedProjectForAssign || !selectedSupervisorId || !siteVisit1Date) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      await api.assignSupervisor(selectedProjectForAssign.id, selectedSupervisorId, siteVisit1Date);
      toast({
        title: 'SuperAligner Assigned',
        description: `SuperAligner assigned to ${selectedProjectForAssign.clientName} with Site Visit 1 on ${siteVisit1Date}`
      });
      setAssignSupervisorDialogOpen(false);
      setSelectedProjectForAssign(null);
      setSelectedSupervisorId('');
      setSiteVisit1Date('');
      fetchAllData();
    } catch (error) {
      console.error('Error assigning supervisor:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign superaligner',
        variant: 'destructive'
      });
    }
  };

  // SV2 proposal helpers
  const fetchSv2MonthAvailability = async (month) => {
    try {
      const year = month.getFullYear();
      const monthNum = month.getMonth() + 1;
      const data = await api.getMonthAvailability(year, monthNum);
      const available = [];
      const unavailable = [];
      data.dates?.forEach(dateInfo => {
        const d = new Date(dateInfo.date);
        if (dateInfo.hasAvailability) available.push(d);
        else unavailable.push(d);
      });
      setSv2MonthAvailability({ availableDates: available, unavailableDates: unavailable });
    } catch (error) {
      console.error('Error fetching month availability:', error);
    }
  };

  const openProposeSv2 = (project) => {
    setSelectedProjectForAssign(project);
    setSv2Date(null);
    setSv2Slots([]);
    setSv2Supervisors([]);
    setSv2SelectedSlotId('');
    setSv2SelectedSupervisorId('');
    setSv2CalendarMonth(new Date());
    setScheduleSv2DialogOpen(true);
    fetchSv2MonthAvailability(new Date());
  };

  const fetchSv2Slots = async (date) => {
    if (!date) return;
    setSv2LoadingSlots(true);
    setSv2Slots([]);
    setSv2SelectedSlotId('');
    setSv2Supervisors([]);
    setSv2SelectedSupervisorId('');
    try {
      const data = await api.getSlotAvailability(date);
      setSv2Slots(data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setSv2LoadingSlots(false);
    }
  };

  const fetchSv2Supervisors = async (date, slotId) => {
    if (!date || !slotId) return;
    setSv2LoadingSupervisors(true);
    setSv2Supervisors([]);
    setSv2SelectedSupervisorId('');
    try {
      const data = await api.getAvailableSupervisors(date, slotId);
      const sups = data.availableSupervisors || [];
      setSv2Supervisors(sups);
      // Auto-select if only one supervisor available
      if (sups.length === 1) {
        setSv2SelectedSupervisorId(String(sups[0].supervisorId));
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    } finally {
      setSv2LoadingSupervisors(false);
    }
  };

  const handleSv2SlotSelect = (slot) => {
    if (!slot.isAvailable || !sv2Date) return;
    setSv2SelectedSlotId(slot.slotId.toString());
    setSv2SelectedSupervisorId('');
    fetchSv2Supervisors(sv2Date, slot.slotId);
  };

  const handleProposeSv2 = async () => {
    if (!selectedProjectForAssign || !sv2Date || !sv2SelectedSlotId || !sv2SelectedSupervisorId) {
      toast({
        title: 'Error',
        description: 'Please select date, slot and supervisor',
        variant: 'destructive'
      });
      return;
    }

    setSv2Submitting(true);
    try {
      const dateStr = `${sv2Date.getFullYear()}-${String(sv2Date.getMonth() + 1).padStart(2, '0')}-${String(sv2Date.getDate()).padStart(2, '0')}`;
      await api.proposeSiteVisit2(selectedProjectForAssign.id, dateStr, parseInt(sv2SelectedSlotId), sv2SelectedSupervisorId);
      toast({
        title: 'SV2 Proposed',
        description: `Site Visit 2 proposed for ${selectedProjectForAssign.clientName}. Awaiting OEM approval.`
      });
      setScheduleSv2DialogOpen(false);
      setSelectedProjectForAssign(null);
      fetchAllData();
    } catch (error) {
      console.error('Error proposing SV2:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to propose Site Visit 2',
        variant: 'destructive'
      });
    } finally {
      setSv2Submitting(false);
    }
  };

  const handleAssignTeam = async () => {
    if (selectedTeamIds.length === 0 || !installationScheduledDate) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one team and a scheduled date',
        variant: 'destructive'
      });
      return;
    }

    if (schedulingInstallation) return;
    setSchedulingInstallation(true);
    try {
      // Build unit names array
      const unitNamesPayload = [];
      const projectEntities = projectDetailForSchedule?.Entities || projectDetailForSchedule?.entities || [];
      projectEntities.forEach(e => {
        const eId = e.Id || e.id;
        const qty = e.Quantity || e.quantity || 1;
        for (let i = 1; i <= qty; i++) {
          const key = `${eId}_${i}`;
          unitNamesPayload.push({
            entityId: eId,
            unitNumber: i,
            unitName: unitNames[key] || ''
          });
        }
      });

      await api.scheduleInstallation(selectedProjectForAssign.id, {
        teamIds: selectedTeamIds,
        scheduledDate: installationScheduledDate,
        estimatedDays: parseInt(estimatedDays) || 5,
        unitNames: unitNamesPayload
      });

      toast({
        title: 'Installation Scheduled',
        description: `Installation scheduled for ${selectedProjectForAssign.clientName} with ${selectedTeamIds.length} team(s)`
      });

      setAssignTeamDialogOpen(false);
      setSelectedProjectForAssign(null);
      setSelectedTeamIds([]);
      setInstallationScheduledDate('');
      setEstimatedDays('5');
      setProjectDetailForSchedule(null);
      setUnitNames({});
      fetchAllData();
    } catch (error) {
      console.error('Error scheduling installation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to schedule installation',
        variant: 'destructive'
      });
    } finally {
      setSchedulingInstallation(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    if (!paymentLinkForm.amount || parseFloat(paymentLinkForm.amount) <= 0) {
      toast({ title: 'Validation Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    setGeneratingPaymentLink(true);
    try {
      const response = await api.post(`/api/projects/${paymentLinkProject.id}/payments/generate-link`, {
        projectId: paymentLinkProject.id,
        amount: parseFloat(paymentLinkForm.amount),
        description: paymentLinkForm.description || undefined,
        expiryHours: paymentLinkForm.expiryHours || 72
      });
      const linkUrl = response.paymentLinkUrl || response.shortUrl;
      if (linkUrl) {
        navigator.clipboard.writeText(linkUrl);
        toast({ title: 'Payment Link Generated', description: 'Link copied to clipboard!' });
      } else {
        toast({ title: 'Payment Link Generated', description: 'Payment link created successfully' });
      }
      setPaymentLinkDialogOpen(false);
      setPaymentLinkForm({ amount: '', description: '', expiryHours: 72 });
      setPaymentLinkProject(null);
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast({ title: 'Error', description: error.message || 'Failed to generate payment link', variant: 'destructive' });
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  const openScheduleSv1 = async (project) => {
    setScheduleSv1Project(project);
    setScheduleSv1Data(null);
    setScheduleSv1DialogOpen(true);
    setScheduleSv1Loading(true);
    try {
      const response = await api.get(`/api/projects/${project.id}/site-visits`);
      setScheduleSv1Data(response?.sv1 || null);
    } catch (error) {
      console.error('Error fetching SV1 data:', error);
      toast({ title: 'Error', description: 'Failed to load SV1 booking details', variant: 'destructive' });
    } finally {
      setScheduleSv1Loading(false);
    }
  };

  const handleScheduleSv1Confirm = async () => {
    if (!scheduleSv1Data || !scheduleSv1Project) return;
    setScheduleSv1Submitting(true);
    try {
      await api.assignSupervisor(
        scheduleSv1Project.id,
        scheduleSv1Data.supervisorId,
        scheduleSv1Data.scheduledDate
      );
      toast({ title: 'Success', description: 'Project moved to SV1 Scheduled' });
      setScheduleSv1DialogOpen(false);
      fetchProjectsData();
    } catch (error) {
      console.error('Error scheduling SV1:', error);
      toast({ title: 'Error', description: error.message || 'Failed to schedule SV1', variant: 'destructive' });
    } finally {
      setScheduleSv1Submitting(false);
    }
  };

  const openScheduleSv2 = async (project) => {
    setScheduleSv2Project(project);
    setScheduleSv2Data(null);
    setScheduleSv2ConfirmOpen(true);
    setScheduleSv2Loading(true);
    try {
      const response = await api.get(`/api/projects/${project.id}/site-visits`);
      setScheduleSv2Data(response?.sv2 || null);
    } catch (error) {
      console.error('Error fetching SV2 data:', error);
      toast({ title: 'Error', description: 'Failed to load SV2 booking details', variant: 'destructive' });
    } finally {
      setScheduleSv2Loading(false);
    }
  };

  const handleScheduleSv2Confirm = async () => {
    if (!scheduleSv2Data || !scheduleSv2Project) return;
    setScheduleSv2Submitting(true);
    try {
      await api.approveSv2(scheduleSv2Project.id, { reschedule: false });
      toast({ title: 'Success', description: 'Project moved to SV2 Scheduled' });
      setScheduleSv2ConfirmOpen(false);
      fetchProjectsData();
    } catch (error) {
      console.error('Error scheduling SV2:', error);
      toast({ title: 'Error', description: error.message || 'Failed to schedule SV2', variant: 'destructive' });
    } finally {
      setScheduleSv2Submitting(false);
    }
  };

  const getNextTeamName = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const existingLetters = teams.map(t => t.name.replace('Aligners ', ''));
    for (const letter of letters) {
      if (!existingLetters.includes(letter)) {
        return `Aligners ${letter}`;
      }
    }
    return `Aligners ${teams.length + 1}`;
  };

  const handleAddTeam = async () => {
    try {
      const teamData = {
        name: newTeam.name,
        skillLevel: newTeam.skillLevel,
        location: newTeam.location
      };

      console.log('Creating team with data:', teamData);

      const response = await api.createTeam(teamData);

      const newTeamData = {
        id: response.id,
        name: response.name,
        skillLevel: response.skillLevel,
        location: response.cityName,
        members: response.members || 0,
        assignedJobs: response.assignedJobs || 0,
        scheduledJobs: 0,
        completedJobs: 0,
        lifetimeRating: 0,
        lastAssigned: null
      };

      setTeams([...teams, newTeamData]);
      toast({
        title: 'Team Created',
        description: `${newTeam.name} has been created successfully`
      });
      setAddTeamDialogOpen(false);
      setNewTeam({ name: '', skillLevel: 'intermediate', location: '' });
    } catch (error) {
      console.error('Error creating team:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create team',
        variant: 'destructive'
      });
    }
  };

  const handleAddMember = async () => {
    try {
      if (!newMember.userId || !newMember.teamId) {
        toast({
          title: 'Validation Error',
          description: 'Please select both user and team',
          variant: 'destructive'
        });
        return;
      }

      const response = await api.addTeamMember(newMember.teamId, newMember.userId);

      toast({
        title: 'Member Added',
        description: response.isActive
          ? `${response.userName} has been added to the team`
          : `${response.userName} has been reactivated in the team`
      });

      setAddMemberDialogOpen(false);
      setNewMember({ userId: '', teamId: '' });

      // Refresh teams data to update member counts
      const teamsData = await api.getTeams();
      const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
      setTeams(teamsList);
    } catch (error) {
      console.error('Error adding member:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add member',
        variant: 'destructive'
      });
    }
  };

  const handleViewMembers = async (team) => {
    try {
      setSelectedTeamForMembers(team);
      const members = await api.getTeamMembersForTeam(team.id);
      setCurrentTeamMembers(members);
      setViewMembersDialogOpen(true);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    try {
      await api.removeTeamMember(selectedTeamForMembers.id, userId);

      toast({
        title: 'Member Removed',
        description: `${userName} has been removed from the team`
      });

      // Refresh team members list
      const members = await api.getTeamMembersForTeam(selectedTeamForMembers.id);
      setCurrentTeamMembers(members);

      // Refresh teams data to update member counts
      const teamsData = await api.getTeams();
      const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
      setTeams(teamsList);
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove member',
        variant: 'destructive'
      });
    }
  };

  const handleOpenAddMemberDialog = async () => {
    try {
      // Fetch available users from API (already filtered by role and team assignment)
      const availableUsersData = await api.getAvailableUsersForTeams();
      setAvailableUsers(availableUsersData);
      setAddMemberDialogOpen(true);
    } catch (error) {
      console.error('Error fetching available users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available users',
        variant: 'destructive'
      });
    }
  };

  const handleEditTeam = async (team) => {
    setEditTeamData({
      id: team.id,
      name: team.name,
      skillLevel: team.skillLevel,
      location: team.cityName
    });
    setEditTeamTab('details');
    setEditTeamNewMemberId('');
    setEditTeamDialogOpen(true);

    // Load members and available users in parallel
    try {
      const [members, users] = await Promise.all([
        api.getTeamMembersForTeam(team.id),
        api.getAvailableUsersForTeams()
      ]);
      setEditTeamMembers(members);
      setEditTeamAvailableUsers(users);
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await api.updateTeam(editTeamData.id, {
        name: editTeamData.name,
        skillLevel: editTeamData.skillLevel,
        location: editTeamData.location
      });

      toast({
        title: 'Success',
        description: 'Team updated successfully'
      });

      setEditTeamDialogOpen(false);

      // Refresh teams data
      const teamsData = await api.getTeams();
      const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
      setTeams(teamsList);
    } catch (error) {
      console.error('Error updating team:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update team',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteTeam = (team) => {
    setTeamToDelete(team);
    setDeleteConfirmDialogOpen(true);
  };

  const confirmDeleteTeam = async () => {
    try {
      await api.deleteTeam(teamToDelete.id);

      toast({
        title: 'Success',
        description: `Team "${teamToDelete.name}" has been deleted and all members removed`
      });

      setDeleteConfirmDialogOpen(false);
      setTeamToDelete(null);

      // Refresh teams data
      const teamsData = await api.getTeams();
      const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
      setTeams(teamsList);
    } catch (error) {
      console.error('Error deleting team:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete team',
        variant: 'destructive'
      });
    }
  };

  // Handle create project
  const handleCreateProject = async (e) => {
    e.preventDefault();

    // Validate entity selection
    if (newProject.selectedEntities.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one entity (Kitchen, Wardrobe, etc.)',
        variant: 'destructive'
      });
      return;
    }

    setCreatingProject(true);
    try {
      const projectData = {
        OemId: newProject.oemId || null,
        ClientName: newProject.clientName,
        ClientPhone: newProject.clientPhone || null,
        SiteAddress: newProject.siteAddress || null,
        CityId: newProject.cityId || null,
        Sqft: parseInt(newProject.sqft),
        PreferredStartDate: newProject.preferredStartDate || null,
        PreferredCompletionDate: newProject.preferredCompletionDate || null,
        EstimatedDays: projectEstimatedDays > 0 ? projectEstimatedDays : null,
        // Send entity selections
        Entities: newProject.selectedEntities.map(e => ({
          EntityId: e.entityId,
          Quantity: e.quantity
        }))
      };

      console.log('Creating project with data:', projectData);

      const response = await api.createProject(projectData);

      toast({
        title: 'Project Created',
        description: `New project for ${newProject.clientName} has been created successfully.`
      });

      setCreateProjectDialogOpen(false);
      setNewProject({
        oemId: '',
        clientName: '',
        clientPhone: '',
        siteAddress: '',
        cityId: '',
        sqft: '',
        preferredStartDate: format(new Date(), 'yyyy-MM-dd'),
        preferredCompletionDate: '',
        selectedEntities: []
      });

      // Refresh data
      fetchAllData();
    } catch (error) {
      console.error('Error creating project:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create project',
        variant: 'destructive'
      });
    } finally {
      setCreatingProject(false);
    }
  };

  const handleOpenTeamMembers = async (team) => {
    try {
      setSelectedTeamForMembers(team);
      const members = await api.getTeamMembersForTeam(team.id);
      setCurrentTeamMembers(members);
      setViewMembersDialogOpen(true);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive'
      });
    }
  };

  const handleOpenOEMProjects = (oem) => {
    setSelectedOEM(oem);
    setOemProjectsDialogOpen(true);
  };

  const getTeamMembersByTeam = (teamName) => {
    return teamMembers.filter(member => member.team === teamName);
  };

  const getOEMProjects = (oemId) => {
    const oemProjects = projects.filter(p => p.oemId === oemId);
    return {
      active: oemProjects.filter(p => {
        const status = p.status || '';
        return status !== StageName.ProjectCompleted && status !== StageName.Completed && status !== StageName.Delivered && status !== StageName.Cancelled;
      }),
      recent: oemProjects.find(p => p.status === StageName.ProjectCompleted || p.status === StageName.Completed || p.status === StageName.Delivered)
    };
  };

  const COMPLETED_STAGES = [StageName.ProjectCompleted, StageName.Completed, StageName.Delivered];
  const isCompletedStage = (status) => COMPLETED_STAGES.includes(status);

  const getAllProjects = () => ({
    all: projects,
    active: projects.filter(p => {
      const status = p.status || '';
      return !isCompletedStage(status) && status !== StageName.Cancelled;
    }),
    completed: projects.filter(p => {
      const status = p.status || '';
      return isCompletedStage(status);
    })
  });

  const handleFilterProjects = (filter) => {
    setProjectsFilter(filter);
    setCurrentPage(1);
  };

  const handleViewProjectDetail = async (project) => {
    setLoadingProjectId(project.id);
    try {
      const response = await api.request(`/api/projects/${project.id}/complete`);
      console.log('Complete Project Response:', response);
      console.log('SV1 Details:', response.sv1Details);
      console.log('SV2 Details:', response.sv2Details);
      console.log('Installation Details:', response.installationDetails);
      setSelectedProjectDetail(response);
      setProjectDetailOpen(true);
    } catch (error) {
      console.error('Error loading project details:', error);
      toast({
        title: "Error",
        description: "Failed to load project details",
        variant: "destructive"
      });
    } finally {
      setLoadingProjectId(null);
    }
  };

  // ─── Project Closure handlers ───
  const handleOpenClosure = async (project) => {
    setClosureProject(project);
    setClosureNotes('');
    setClosureItemPhotos({});
    setClosurePhotosExpanded({});
    setClosureEnlargedPhoto(null);
    setClosureDialogOpen(true);
    setClosureLoading(true);
    try {
      const details = await api.getAdminSignoffDetails(project.id);
      setClosureDetails(details);
    } catch (error) {
      console.error('Error loading closure details:', error);
      toast({ title: 'Error', description: 'Failed to load sign-off details', variant: 'destructive' });
    } finally {
      setClosureLoading(false);
    }
  };

  const loadClosureItemPhotos = async (projectId, responseId) => {
    if (closureItemPhotos[responseId]) {
      setClosurePhotosExpanded(prev => ({ ...prev, [responseId]: !prev[responseId] }));
      return;
    }
    try {
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const docsRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents?referenceId=${responseId}`, { headers });
      if (!docsRes.ok) throw new Error('Failed to fetch documents');
      const data = await docsRes.json();
      const docsList = data.documents || data || [];
      const photos = [];
      for (const doc of docsList) {
        const streamRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${doc.id}/stream`, { headers });
        if (streamRes.ok) {
          const blob = await streamRes.blob();
          photos.push({ id: doc.id, url: URL.createObjectURL(blob), fileName: doc.fileName });
        }
      }
      setClosureItemPhotos(prev => ({ ...prev, [responseId]: photos }));
      setClosurePhotosExpanded(prev => ({ ...prev, [responseId]: true }));
    } catch (error) {
      console.error('Error loading closure photos:', error);
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  const handleCloseProject = async () => {
    setClosureSubmitting(true);
    try {
      await api.adminCloseProject(closureProject.id, closureNotes);
      toast({ title: 'Project Closed', description: `${closureProject.clientName} has been closed successfully` });
      setClosureDialogOpen(false);
      fetchProjectsData();
    } catch (error) {
      console.error('Error closing project:', error);
      toast({ title: 'Error', description: error.message || 'Failed to close project', variant: 'destructive' });
    } finally {
      setClosureSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      // Initial stages
      [StageName.NewProject]: 'bg-slate-100 text-slate-700 border-slate-300',
      [StageName.QuotationRejected]: 'bg-red-100 text-red-700 border-red-300',
      [StageName.QuotePending]: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Quote Sent': 'bg-yellow-100 text-yellow-700 border-yellow-300',

      // Site Visit 1
      [StageName.Sv1Pending]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv1Scheduled]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv1Completed]: 'bg-sky-100 text-sky-700 border-sky-300',
      'SV1 Complete': 'bg-sky-100 text-sky-700 border-sky-300',

      // Site Visit 2
      [StageName.Sv2Proposed]: 'bg-amber-100 text-amber-700 border-amber-300',
      [StageName.Sv2Pending]: 'bg-orange-100 text-orange-700 border-orange-300',
      [StageName.Sv2Scheduled]: 'bg-orange-100 text-orange-700 border-orange-300',
      [StageName.Sv2Completed]: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'SV2 Complete': 'bg-indigo-100 text-indigo-700 border-indigo-300',

      // Installation stages
      [StageName.InstallationScheduled]: 'bg-blue-100 text-blue-700 border-blue-300',
      [StageName.Scheduled]: 'bg-blue-100 text-blue-700 border-blue-300',
      [StageName.InstallationInProgress]: 'bg-purple-100 text-purple-700 border-purple-300',
      [StageName.InProgress]: 'bg-purple-100 text-purple-700 border-purple-300',
      [StageName.InstallationCompleted]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.ClientReview]: 'bg-cyan-100 text-cyan-700 border-cyan-300',

      // Completion stages
      [StageName.Completed]: 'bg-green-100 text-green-700 border-green-300',
      [StageName.Delivered]: 'bg-emerald-100 text-emerald-700 border-emerald-300',

      // Other statuses
      [StageName.OnHold]: 'bg-red-100 text-red-700 border-red-300',
      [StageName.Cancelled]: 'bg-gray-100 text-gray-700 border-gray-300',
      [StageName.PendingApproval]: 'bg-pink-100 text-pink-700 border-pink-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shrink-0">
                <LayoutDashboard className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">Platform overview and management</p>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-1 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-gray-600">
                <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
              </Button>
              <span className="text-gray-400 hidden sm:inline">/</span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} className="text-gray-600">
                <SettingsIcon className="w-4 h-4 mr-1" /> Settings
              </Button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Sq.Ft This Month</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{kpis.totalSqftThisMonth?.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-teal-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm">
                <span className="text-green-600 font-medium">+12%</span>
                <span className="text-gray-600">from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active OEMs</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{kpis.activeOEMs}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals & Payments Section */}
        {(projects.filter(p => p.oemApprovalStatus === 'pending').length > 0 ||
          projects.filter(p => p.paymentStatus === PaymentStatus.Pending && p.status !== StageName.QuotePending).length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Pending OEM Approvals */}
              {projects.filter(p => p.oemApprovalStatus === 'pending').length > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-orange-700">
                      <Clock className="w-5 h-5" />
                      Pending OEM Approvals
                    </CardTitle>
                    <CardDescription>Projects awaiting OEM approval for quote/schedule changes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {projects.filter(p => p.oemApprovalStatus === 'pending').map(project => (
                        <div key={project.id} className="p-3 bg-white rounded-lg border border-orange-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{project.clientName}</p>
                              <p className="text-sm text-gray-500">{project.location}</p>
                            </div>
                            <div className="text-right">
                              {project.revisedQuote && (
                                <p className="font-semibold text-orange-600">₹{project.revisedQuote?.toLocaleString()}</p>
                              )}
                              <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">
                                Awaiting Approval
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending Payments */}
              {projects.filter(p => p.status === StageName.Completed || p.status === StageName.Scheduled).length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <CreditCard className="w-5 h-5" />
                      Payment Status
                    </CardTitle>
                    <CardDescription>Track booking and balance payments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {projects.filter(p => [StageName.Scheduled, StageName.InProgress, StageName.Completed].includes(p.status)).slice(0, 5).map(project => (
                        <div key={project.id} className="p-3 bg-white rounded-lg border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{project.clientName}</p>
                              <p className="text-sm text-gray-500">{project.oemName}</p>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 rounded-full ${project.status === StageName.Completed
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                                }`}>
                                {project.status === StageName.Completed ? 'Balance Due' : 'Booking Paid'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 sm:inline-flex w-full sm:w-auto h-auto sm:h-10 gap-1 mb-6 sm:mb-8">
            <TabsTrigger value="projects" className="text-xs sm:text-sm">Projects</TabsTrigger>
            <TabsTrigger value="supervisors" className="text-xs sm:text-sm">SuperAligners</TabsTrigger>
            <TabsTrigger value="teams" className="text-xs sm:text-sm">Teams</TabsTrigger>
            <TabsTrigger value="oems" className="text-xs sm:text-sm">OEMs</TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {projectsLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading projects...</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <CardTitle>Project Overview</CardTitle>
                        <CardDescription>All installation projects across the platform</CardDescription>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => navigate('/admin/scheduler')}>
                          View Scheduler
                        </Button>
                        <Button
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                          onClick={() => navigate('/admin/projects/create')}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Project
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-6">
                      <div
                        className="p-3 sm:p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => handleFilterProjects('all')}
                      >
                        <div className="text-xs sm:text-sm font-medium text-blue-800">Total Projects</div>
                        <div className="text-2xl sm:text-4xl font-bold text-blue-900 mt-1 sm:mt-2">{kpis.totalProjects}</div>
                      </div>
                      <div
                        className="p-3 sm:p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => handleFilterProjects('active')}
                      >
                        <div className="text-xs sm:text-sm font-medium text-purple-800">Active Projects</div>
                        <div className="text-2xl sm:text-4xl font-bold text-purple-900 mt-1 sm:mt-2">{kpis.activeProjects}</div>
                      </div>
                      <div
                        className="p-3 sm:p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 cursor-pointer hover:shadow-lg transition-shadow col-span-2 md:col-span-1"
                        onClick={() => handleFilterProjects('completed')}
                      >
                        <div className="text-xs sm:text-sm font-medium text-green-800">Completed</div>
                        <div className="text-2xl sm:text-4xl font-bold text-green-900 mt-1 sm:mt-2">{kpis.completedProjects}</div>
                      </div>
                    </div>

                    {/* Projects Table */}
                    <div className="border rounded-lg">
                      <div className="p-4 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900">
                          {projectsFilter === 'all' && 'All Projects'}
                          {projectsFilter === 'active' && 'Active Projects'}
                          {projectsFilter === 'completed' && 'Completed Projects'}
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <Table className="min-w-[900px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="whitespace-nowrap">Client</TableHead>
                              <TableHead className="whitespace-nowrap">Location</TableHead>
                              <TableHead className="whitespace-nowrap">Sqft</TableHead>
                              <TableHead className="whitespace-nowrap">OEM</TableHead>
                              <TableHead className="whitespace-nowrap">Supervisor</TableHead>
                              <TableHead className="whitespace-nowrap">Status</TableHead>
                              <TableHead className="whitespace-nowrap">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(() => {
                              const filteredProjects = getAllProjects()[projectsFilter] || [];
                              const indexOfLastProject = currentPage * projectsPerPage;
                              const indexOfFirstProject = indexOfLastProject - projectsPerPage;
                              const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

                              if (currentProjects.length === 0) {
                                return (
                                  <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                      No {projectsFilter === 'all' ? '' : projectsFilter} projects found
                                    </TableCell>
                                  </TableRow>
                                );
                              }

                              return currentProjects.map((project) => (
                                <TableRow key={project.id}>
                                  <TableCell className="font-medium whitespace-nowrap">{project.clientName}</TableCell>
                                  <TableCell className="whitespace-nowrap">{project.location || 'N/A'}</TableCell>
                                  <TableCell className="whitespace-nowrap">{project.sqft}</TableCell>
                                  <TableCell className="whitespace-nowrap">{project.oemName || 'N/A'}</TableCell>
                                  <TableCell className="whitespace-nowrap">{project.supervisorName || '-'}</TableCell>
                                  <TableCell>
                                    <span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap inline-block ${getStatusColor(project.status)}`}>
                                      {project.status}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-2 whitespace-nowrap">
                                      {[StageName.NewProject, StageName.QuotationRejected].includes(project.status) && (
                                        project.draftQuotation ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300"
                                            onClick={async () => {
                                              // Lazy-fetch full draft quotation + full project (entities) when user clicks Edit
                                              try {
                                                const [response, fullProject] = await Promise.all([
                                                  api.get(`/api/projects/${project.id}/quotations`),
                                                  api.get(`/api/projects/${project.id}`)
                                                ]);
                                                const quotations = response?.quotations || response?.Quotations || [];
                                                const draft = quotations.find(q =>
                                                  q.status === QuotationStatus.Draft &&
                                                  (q.quotationType === QuotationType.BaseWork || q.QuotationType === QuotationType.BaseWork)
                                                );
                                                setSelectedProjectForQuotation({ ...project, ...fullProject, draftQuotation: draft || null });
                                              } catch (err) {
                                                console.error('Failed to load draft quotation:', err);
                                                setSelectedProjectForQuotation(project);
                                              }
                                              setQuotationModalOpen(true);
                                            }}
                                          >
                                            Edit Draft Quotation
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="bg-teal-50 hover:bg-teal-100 text-teal-700 border-teal-300"
                                            onClick={async () => {
                                              // Fetch full project (including entities) for the quotation modal
                                              try {
                                                const fullProject = await api.get(`/api/projects/${project.id}`);
                                                setSelectedProjectForQuotation({ ...project, ...fullProject });
                                              } catch (err) {
                                                console.error('Failed to load project entities:', err);
                                                setSelectedProjectForQuotation(project);
                                              }
                                              setQuotationModalOpen(true);
                                            }}
                                          >
                                            Prepare Quotation
                                          </Button>
                                        )
                                      )}
                                      {project.status === StageName.Sv1Completed && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => openProposeSv2(project)}
                                        >
                                          Propose SV2
                                        </Button>
                                      )}
                                      {project.status === StageName.Sv2Proposed && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300"
                                          onClick={() => openScheduleSv2(project)}
                                        >
                                          <Calendar className="w-4 h-4 mr-1" />
                                          Schedule SV2
                                        </Button>
                                      )}
                                      {project.status === StageName.Sv2Completed && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="bg-teal-50 hover:bg-teal-100"
                                          onClick={async () => {
                                            setSelectedProjectForAssign(project);
                                            setScheduleLoading(true);
                                            setAssignTeamDialogOpen(true);
                                            try {
                                              const detail = await api.getProject(project.id);
                                              setProjectDetailForSchedule(detail);
                                              // Initialize unit names with defaults
                                              const names = {};
                                              (detail.Entities || detail.entities || []).forEach(e => {
                                                const eName = e.Name || e.name;
                                                const qty = e.Quantity || e.quantity || 1;
                                                const eId = e.Id || e.id;
                                                for (let i = 1; i <= qty; i++) {
                                                  names[`${eId}_${i}`] = `${eName} ${i}`;
                                                }
                                              });
                                              setUnitNames(names);
                                              // Fetch teams if not loaded
                                              if (teams.length === 0) await fetchTeamsData();
                                            } catch (err) {
                                              console.error('Error loading project details:', err);
                                            } finally {
                                              setScheduleLoading(false);
                                            }
                                          }}
                                        >
                                          Schedule Installation
                                        </Button>
                                      )}
                                      {project.status === StageName.QuotationApproved && (
                                        <>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300"
                                            onClick={() => openScheduleSv1(project)}
                                          >
                                            <Calendar className="w-4 h-4 mr-1" />
                                            Schedule SV1
                                          </Button>
                                        </>
                                      )}

                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => navigate(`/projects/${project.id}/view`)}
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        View
                                      </Button>
                                      {project.status === StageName.PendingAdminClosure && (
                                        <Button
                                          size="sm"
                                          className="bg-green-600 hover:bg-green-700 text-white"
                                          onClick={() => handleOpenClosure(project)}
                                        >
                                          <CheckCircle2 className="w-4 h-4 mr-1" />
                                          Close Project
                                        </Button>
                                      )}
                                      {!project.hardwareListUploaded && [
                                        StageName.Sv1Scheduled, StageName.Sv1Completed,
                                        StageName.Sv2Proposed, StageName.Sv2Scheduled, StageName.Sv2Completed,
                                        StageName.InstallationScheduled, StageName.InstallationInProgress,
                                        StageName.InstallationCompleted, StageName.ClientReview, StageName.PendingOemSignoff,
                                        StageName.PendingAdminClosure, StageName.ProjectCompleted,
                                        'SV1 In Progress', 'SV2 In Progress'
                                      ].includes(project.status) && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100"
                                            onClick={() => navigate(`/projects/${project.id}/view?tab=documents`)}
                                          >
                                            <Wrench className="w-4 h-4 mr-1" />
                                            Hardware Pending
                                          </Button>
                                        )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ));
                            })()}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      {(() => {
                        const filteredProjects = getAllProjects()[projectsFilter] || [];
                        const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

                        if (totalPages <= 1) return null;

                        return (
                          <div className="flex items-center justify-between p-4 border-t">
                            <div className="text-sm text-gray-500">
                              Showing {((currentPage - 1) * projectsPerPage) + 1} to {Math.min(currentPage * projectsPerPage, filteredProjects.length)} of {filteredProjects.length} projects
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </Button>
                              <div className="flex items-center gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                  <Button
                                    key={page}
                                    size="sm"
                                    variant={currentPage === page ? "default" : "outline"}
                                    onClick={() => setCurrentPage(page)}
                                    className={currentPage === page ? "bg-teal-600 hover:bg-teal-700" : ""}
                                  >
                                    {page}
                                  </Button>
                                ))}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>


              </>
            )}
          </TabsContent>
          <TabsContent value="supervisors">
            {supervisorsLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading supervisors...</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>SuperAligners</CardTitle>
                      <CardDescription>Manage site visit superaligners and track their project assignments</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Supervisor Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    <div className="p-3 sm:p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200">
                      <div className="text-xs sm:text-sm font-medium text-amber-700">Total Supervisors</div>
                      <div className="text-2xl sm:text-3xl font-bold text-amber-800 mt-1">{supervisors.length}</div>
                    </div>
                    <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                      <div className="text-xs sm:text-sm font-medium text-blue-700">Active Projects</div>
                      <div className="text-2xl sm:text-3xl font-bold text-blue-800 mt-1">
                        {supervisors.reduce((sum, s) => sum + (s.activeProjects || 0), 0)}
                      </div>
                    </div>
                    <div className="p-3 sm:p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                      const allSv1 = supervisors.flatMap(s => (s.sv1ScheduledProjects || []).map(p => ({ ...p, supervisorName: s.name })));
                      setSvScheduledProjects(allSv1);
                      setSvScheduledType('SV1');
                      setSvScheduledDialogOpen(true);
                    }}>
                      <div className="text-xs sm:text-sm font-medium text-yellow-700">SV1 Scheduled</div>
                      <div className="text-2xl sm:text-3xl font-bold text-yellow-800 mt-1">
                        {supervisors.reduce((sum, s) => sum + (s.sv1Scheduled || 0), 0)}
                      </div>
                    </div>
                    <div className="p-3 sm:p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                      const allSv2 = supervisors.flatMap(s => (s.sv2ScheduledProjects || []).map(p => ({ ...p, supervisorName: s.name })));
                      setSvScheduledProjects(allSv2);
                      setSvScheduledType('SV2');
                      setSvScheduledDialogOpen(true);
                    }}>
                      <div className="text-xs sm:text-sm font-medium text-orange-700">SV2 Scheduled</div>
                      <div className="text-2xl sm:text-3xl font-bold text-orange-800 mt-1">
                        {supervisors.reduce((sum, s) => sum + (s.sv2Scheduled || 0), 0)}
                      </div>
                    </div>
                  </div>

                  {/* Supervisors Table */}
                  <div className="rounded-md border overflow-x-auto">
                    <Table className="min-w-[1100px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Name</TableHead>
                          <TableHead className="whitespace-nowrap">Email</TableHead>
                          <TableHead className="whitespace-nowrap">Phone</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Active Projects</TableHead>
                          <TableHead className="text-center whitespace-nowrap">SV1 Scheduled</TableHead>
                          <TableHead className="text-center whitespace-nowrap">SV1 Done</TableHead>
                          <TableHead className="text-center whitespace-nowrap">SV2 Scheduled</TableHead>
                          <TableHead className="text-center whitespace-nowrap">SV2 Done</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {supervisors.slice(
                          (supervisorsCurrentPage - 1) * supervisorsPerPage,
                          supervisorsCurrentPage * supervisorsPerPage
                        ).map((supervisor) => (
                          <TableRow key={supervisor.id}>
                            <TableCell className="font-medium">{supervisor.name}</TableCell>
                            <TableCell>{supervisor.email}</TableCell>
                            <TableCell>{supervisor.phone || 'N/A'}</TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 font-semibold">
                                {supervisor.activeProjects}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-800 font-semibold cursor-pointer" onClick={() => {
                                setSvScheduledProjects((supervisor.sv1ScheduledProjects || []).map(p => ({ ...p, supervisorName: supervisor.name })));
                                setSvScheduledType('SV1');
                                setSvScheduledDialogOpen(true);
                              }}>
                                {supervisor.sv1Scheduled || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-800 font-semibold">
                                {supervisor.sv1Completed || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-800 font-semibold cursor-pointer" onClick={() => {
                                setSvScheduledProjects((supervisor.sv2ScheduledProjects || []).map(p => ({ ...p, supervisorName: supervisor.name })));
                                setSvScheduledType('SV2');
                                setSvScheduledDialogOpen(true);
                              }}>
                                {supervisor.sv2Scheduled || 0}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-800 font-semibold">
                                {supervisor.sv2Completed || 0}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {supervisors.length > supervisorsPerPage && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-gray-500">
                        Showing {((supervisorsCurrentPage - 1) * supervisorsPerPage) + 1} to {Math.min(supervisorsCurrentPage * supervisorsPerPage, supervisors.length)} of {supervisors.length} supervisors
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSupervisorsCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={supervisorsCurrentPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSupervisorsCurrentPage(prev => Math.min(Math.ceil(supervisors.length / supervisorsPerPage), prev + 1))}
                          disabled={supervisorsCurrentPage >= Math.ceil(supervisors.length / supervisorsPerPage)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="teams">
            {teamsLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading teams...</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle>Aligner Teams</CardTitle>
                      <CardDescription>Manage installation teams and members</CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={handleOpenAddMemberDialog}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add Member
                      </Button>
                      <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setAddTeamDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Team
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {teams.map((team) => (
                      <div key={team.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          {/* Team Name and Info */}
                          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-lg truncate">{team.name}</h3>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${team.skillLevel === 'senior' ? 'bg-green-100 text-green-700' :
                              team.skillLevel === 'junior' ? 'bg-yellow-100 text-yellow-700' :
                                team.skillLevel === 'expert' ? 'bg-purple-100 text-purple-700' :
                                  'bg-blue-100 text-blue-700'
                              }`}>
                              {team.skillLevel.charAt(0).toUpperCase() + team.skillLevel.slice(1)}
                            </span>
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <MapPin className="w-4 h-4" />
                              <span>{team.cityName}</span>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-6 sm:gap-6">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-yellow-700">{team.assignedJobs || 0}</p>
                              <p className="text-xs text-gray-500">Active Jobs</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-700">{team.members || 0}</p>
                              <p className="text-xs text-gray-500">Team Members</p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleEditTeam(team)} className="whitespace-nowrap">
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDeleteTeam(team)} className="text-red-600 hover:text-red-700 hover:bg-red-50 whitespace-nowrap">
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="oems">
            {oemsLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading OEMs...</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>OEM Partners</CardTitle>
                  <CardDescription>Manage OEM relationships and projects</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table className="min-w-[800px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">OEM Name</TableHead>
                          <TableHead className="whitespace-nowrap">Email</TableHead>
                          <TableHead className="whitespace-nowrap">Phone</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Total Projects</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Active Projects</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {oems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-gray-500">
                              No OEMs found
                            </TableCell>
                          </TableRow>
                        ) : (
                          oems
                            .slice((oemsCurrentPage - 1) * oemsPerPage, oemsCurrentPage * oemsPerPage)
                            .map((oem) => (
                              <TableRow key={oem.id}>
                                <TableCell className="font-medium">
                                  {oem.name}
                                </TableCell>
                                <TableCell>{oem.email}</TableCell>
                                <TableCell>{oem.phone || '-'}</TableCell>
                                <TableCell className="text-center">
                                  <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                                    {oem.totalProjects}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-700">
                                    {oem.activeProjects}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenOEMProjects(oem)}
                                  >
                                    View Projects
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* OEMs Pagination */}
                  {oems.length > oemsPerPage && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-sm text-gray-500">
                        Showing {((oemsCurrentPage - 1) * oemsPerPage) + 1} to {Math.min(oemsCurrentPage * oemsPerPage, oems.length)} of {oems.length} OEMs
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOemsCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={oemsCurrentPage === 1}
                        >
                          Previous
                        </Button>
                        {Array.from({ length: Math.ceil(oems.length / oemsPerPage) }, (_, i) => i + 1).map(page => (
                          <Button
                            key={page}
                            variant={page === oemsCurrentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setOemsCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOemsCurrentPage(prev => Math.min(Math.ceil(oems.length / oemsPerPage), prev + 1))}
                          disabled={oemsCurrentPage === Math.ceil(oems.length / oemsPerPage)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Team Dialog */}
        <Dialog open={addTeamDialogOpen} onOpenChange={setAddTeamDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Team</DialogTitle>
              <DialogDescription>Create a new aligner team</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 pb-2">
              <div className="space-y-2">
                <Label>Team Name</Label>
                <Input
                  value={newTeam.name || ''}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  placeholder="Enter team name (e.g., Bangalore Expert Team)"
                />
              </div>
              <div className="space-y-2">
                <Label>Skill Level</Label>
                <Select value={newTeam.skillLevel} onValueChange={(v) => setNewTeam({ ...newTeam, skillLevel: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {skillLevels.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={newTeam.location} onValueChange={(v) => setNewTeam({ ...newTeam, location: v })}>
                  <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    {cities.map(city => (
                      <SelectItem key={city.id} value={city.cityName}>
                        {city.cityName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={handleAddTeam} disabled={!newTeam.name || !newTeam.skillLevel || !newTeam.location}>
                Create Team
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Member Dialog */}
        <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription>Assign a user to a team</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 pb-2">
              <div className="space-y-2">
                <Label>User *</Label>
                <Select value={newMember.userId} onValueChange={(v) => setNewMember({ ...newMember, userId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {availableUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team *</Label>
                <Select value={newMember.teamId} onValueChange={(v) => setNewMember({ ...newMember, teamId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} - {team.cityName} ({team.skillLevel})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={handleAddMember}
                disabled={!newMember.userId || !newMember.teamId}
              >
                Add Member
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Members Dialog */}
        <Dialog open={viewMembersDialogOpen} onOpenChange={setViewMembersDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Team Members - {selectedTeamForMembers?.name}</DialogTitle>
              <DialogDescription>
                {selectedTeamForMembers?.cityName} • {selectedTeamForMembers?.skillLevel}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {currentTeamMembers.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No members in this team</p>
              ) : (
                currentTeamMembers.map((member) => (
                  <div key={member.userId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{member.userName}</h4>
                        {!member.isActive && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Inactive</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{member.email}</p>
                      <div className="flex gap-4 mt-1 text-xs text-gray-500">
                        {member.phoneNumber && <span>📞 {member.phoneNumber}</span>}
                        {member.role && <span>👤 {member.role}</span>}
                        <span>📅 Joined: {new Date(member.joinedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {member.isActive && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveMember(member.userId, member.userName)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Ratings Dialog */}
        <Dialog open={ratingsDialogOpen} onOpenChange={setRatingsDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Recently Completed Projects</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {recentlyCompleted.map(project => (
                <div key={project.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{project.clientName}</p>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < (project.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">{project.location}</p>
                  <p className="text-xs text-gray-400 mt-1">Completed: {formatDate(project.completedDate)}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Team Members Dialog */}
        <Dialog open={teamMembersDialogOpen} onOpenChange={setTeamMembersDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedTeam?.name} - Team Members</DialogTitle>
              <DialogDescription>Supervisor: {selectedTeam?.supervisor}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {selectedTeam && getTeamMembersByTeam(selectedTeam.name).map(member => (
                <div key={member.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-gray-500">{member.designation || member.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{member.phone}</p>
                      <p className="text-xs text-gray-500">{member.skill} • {member.location}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>Age: {member.age}</span>
                    <span>Blood: {member.bloodGroup}</span>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* OEM Projects Dialog */}
        <Dialog open={oemProjectsDialogOpen} onOpenChange={setOemProjectsDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedOEM?.name} - Projects</DialogTitle>
            </DialogHeader>
            {selectedOEM && (
              <div className="space-y-4 mt-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Active Projects</p>
                  {getOEMProjects(selectedOEM.id).active.map(project => (
                    <div key={project.id} className="p-3 border rounded-lg mb-2">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{project.clientName}</p>
                        <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(project.status)}`}>
                          {project.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{project.location} • {project.sqft} sq.ft</p>
                    </div>
                  ))}
                  {getOEMProjects(selectedOEM.id).active.length === 0 && (
                    <p className="text-sm text-gray-500">No active projects</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign SuperAligner Dialog */}
        <Dialog open={assignSupervisorDialogOpen} onOpenChange={setAssignSupervisorDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assign SuperAligner</DialogTitle>
              <DialogDescription>
                Assign a SuperAligner to {selectedProjectForAssign?.clientName} and schedule Site Visit 1
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 pb-2">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Project Details:</p>
                <p className="font-medium">{selectedProjectForAssign?.clientName}</p>
                <p className="text-sm text-gray-500">{selectedProjectForAssign?.location} • {selectedProjectForAssign?.sqft} sq.ft</p>
              </div>

              <div className="space-y-2">
                <Label>Select SuperAligner *</Label>
                <Select value={selectedSupervisorId} onValueChange={setSelectedSupervisorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a superaligner" />
                  </SelectTrigger>
                  <SelectContent>
                    {supervisors.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Site Visit 1 Date *</Label>
                <Input
                  type="date"
                  value={siteVisit1Date}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && isHoliday(new Date(val + 'T00:00:00'))) {
                      toast({ title: 'Holiday', description: `${getHolidayName(new Date(val + 'T00:00:00')) || val} is a holiday. Please select another date.`, variant: 'destructive' });
                      return;
                    }
                    setSiteVisit1Date(val);
                  }}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <Button
                className="w-full bg-amber-600 hover:bg-amber-700"
                onClick={handleAssignSupervisor}
                disabled={!selectedSupervisorId || !siteVisit1Date}
                data-testid="confirm-assign-supervisor"
              >
                Assign & Schedule SV1
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Schedule Site Visit 2 Dialog */}
        <Dialog open={scheduleSv2DialogOpen} onOpenChange={setScheduleSv2DialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Propose Site Visit 2</DialogTitle>
              <DialogDescription>
                Propose a date, time slot and supervisor for SV2 for {selectedProjectForAssign?.clientName}. OEM will need to approve.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 pb-2">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Project Details:</p>
                <p className="font-medium">{selectedProjectForAssign?.clientName}</p>
                <p className="text-sm text-gray-500">{selectedProjectForAssign?.location}</p>
                <p className="text-sm text-gray-500">Supervisor: {selectedProjectForAssign?.supervisorName}</p>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Site Visit 2 should be scheduled 2-3 days before the proposed start date,
                  or on confirmation of material arrival from OEM.
                </p>
              </div>

              {/* Date picker with availability */}
              <div className="space-y-2">
                <Label>Select Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {sv2Date ? format(sv2Date, 'PPP') : 'Select a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarUI
                      mode="single"
                      selected={sv2Date}
                      onSelect={(date) => {
                        setSv2Date(date);
                        if (date) fetchSv2Slots(date);
                      }}
                      disabled={(date) => date < new Date() || isHoliday(date)}
                      month={sv2CalendarMonth}
                      onMonthChange={(month) => {
                        setSv2CalendarMonth(month);
                        fetchSv2MonthAvailability(month);
                        onHolidayMonthChange(month);
                      }}
                      modifiers={{
                        available: sv2MonthAvailability.availableDates,
                        unavailable: sv2MonthAvailability.unavailableDates,
                        holiday: holidayDates
                      }}
                      modifiersClassNames={{
                        available: 'bg-green-100 text-green-900 hover:bg-green-200',
                        unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                        holiday: 'holiday-day'
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time slots */}
              {sv2Date && (
                <div className="space-y-2">
                  <Label>Select Time Slot *</Label>
                  {sv2LoadingSlots ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading slots...
                    </div>
                  ) : sv2Slots.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {sv2Slots.map(slot => (
                        <Button
                          key={slot.slotId}
                          variant="outline"
                          className={`flex-col h-auto py-2 items-center ${sv2SelectedSlotId === slot.slotId.toString()
                            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                            : slot.isAvailable
                              ? 'border-green-400 hover:bg-green-50'
                              : 'border-red-300 bg-red-50 opacity-60 cursor-not-allowed'
                            }`}
                          onClick={() => handleSv2SlotSelect(slot)}
                          disabled={!slot.isAvailable}
                        >
                          <span className="font-medium text-sm">{slot.slotName?.split('(')[0]?.trim()}</span>
                          <Badge className={`mt-1 text-xs ${slot.isAvailable ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-red-500 text-white'}`}>
                            {slot.isAvailable ? `${slot.availableSupervisors} available` : 'Fully Booked'}
                          </Badge>
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No slots available for this date</p>
                  )}
                </div>
              )}

              {/* Supervisor selection */}
              {sv2SelectedSlotId && (
                <div className="space-y-2">
                  <Label>Select SuperAligner *</Label>
                  {sv2LoadingSupervisors ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading supervisors...
                    </div>
                  ) : sv2Supervisors.length > 0 ? (
                    <Select value={sv2SelectedSupervisorId} onValueChange={(val) => setSv2SelectedSupervisorId(val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a supervisor" />
                      </SelectTrigger>
                      <SelectContent>
                        {sv2Supervisors.map(sup => (
                          <SelectItem key={String(sup.supervisorId)} value={String(sup.supervisorId)}>
                            {sup.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-red-600">No supervisors available for this slot</p>
                  )}
                </div>
              )}

              <Button
                className="w-full bg-sky-600 hover:bg-sky-700"
                onClick={handleProposeSv2}
                disabled={!sv2Date || !sv2SelectedSlotId || !sv2SelectedSupervisorId || sv2Submitting}
                data-testid="confirm-propose-sv2"
              >
                {sv2Submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Calendar className="w-4 h-4 mr-2" />
                Propose Site Visit 2
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Schedule Installation Dialog */}
        <Dialog open={assignTeamDialogOpen} onOpenChange={(open) => {
          setAssignTeamDialogOpen(open);
          if (!open) {
            setSelectedTeamIds([]);
            setInstallationScheduledDate('');
            setEstimatedDays('5');
            setProjectDetailForSchedule(null);
            setUnitNames({});
          }
        }}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule Installation</DialogTitle>
              <DialogDescription>
                Schedule installation for {selectedProjectForAssign?.clientName}
              </DialogDescription>
            </DialogHeader>
            {scheduleLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Loading project details...</span>
              </div>
            ) : (
              <div className="space-y-4 mt-4 pb-2">
                {/* Project Info */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-1">
                  <p className="font-medium text-base">{selectedProjectForAssign?.clientName}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                    <span>{selectedProjectForAssign?.sqft || projectDetailForSchedule?.Sqft || projectDetailForSchedule?.sqft} sq.ft</span>
                    {(projectDetailForSchedule?.PreferredStartDate || projectDetailForSchedule?.preferredStartDate) && (
                      <span>Start: {new Date(projectDetailForSchedule.PreferredStartDate || projectDetailForSchedule.preferredStartDate).toLocaleDateString()}</span>
                    )}
                    {(projectDetailForSchedule?.PreferredCompletionDate || projectDetailForSchedule?.preferredCompletionDate) && (
                      <span>End: {new Date(projectDetailForSchedule.PreferredCompletionDate || projectDetailForSchedule.preferredCompletionDate).toLocaleDateString()}</span>
                    )}
                    {(projectDetailForSchedule?.EstimatedDays || projectDetailForSchedule?.estimatedDays) && (
                      <span>Est. {projectDetailForSchedule.EstimatedDays || projectDetailForSchedule.estimatedDays} days</span>
                    )}
                  </div>
                </div>

                {/* Team Selection - Checkboxes */}
                <div className="space-y-2">
                  <Label>Select Teams * <span className="text-xs text-gray-400 font-normal">(multiple allowed)</span></Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {teams.filter(t => t.isActive !== false).map(team => (
                      <label key={team.id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                        <Checkbox
                          checked={selectedTeamIds.includes(team.id)}
                          onCheckedChange={(checked) => {
                            setSelectedTeamIds(prev =>
                              checked ? [...prev, team.id] : prev.filter(id => id !== team.id)
                            );
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{team.name}</p>
                          <p className="text-xs text-gray-500">{team.cityName} • {team.skillLevel} • {team.members} members</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedTeamIds.length > 0 && (
                    <p className="text-xs text-teal-600">{selectedTeamIds.length} team(s) selected</p>
                  )}
                </div>

                {/* Date & Days */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Installation Start Date *</Label>
                    <Input
                      type="date"
                      value={installationScheduledDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val && isHoliday(new Date(val + 'T00:00:00'))) {
                          toast({ title: 'Holiday', description: `${getHolidayName(new Date(val + 'T00:00:00')) || val} is a holiday. Please select another date.`, variant: 'destructive' });
                          return;
                        }
                        setInstallationScheduledDate(val);
                      }}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Days</Label>
                    <Input
                      type="number"
                      value={estimatedDays}
                      onChange={(e) => setEstimatedDays(e.target.value)}
                      min="1"
                      max="90"
                      placeholder="5"
                    />
                  </div>
                </div>

                {/* Entity Unit Names */}
                {(() => {
                  const projectEntities = projectDetailForSchedule?.Entities || projectDetailForSchedule?.entities || [];
                  if (projectEntities.length === 0) return null;
                  return (
                    <div className="space-y-3">
                      <Label>Unit Names <span className="text-xs text-gray-400 font-normal">(optional — defaults auto-generated)</span></Label>
                      {projectEntities.map(entity => {
                        const eName = entity.Name || entity.name;
                        const eId = entity.Id || entity.id;
                        const qty = entity.Quantity || entity.quantity || 1;
                        return (
                          <div key={eId} className="border rounded-lg p-3 space-y-2">
                            <p className="text-sm font-medium">{eName} <span className="text-gray-400">× {qty}</span></p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {Array.from({ length: qty }, (_, i) => i + 1).map(unitNum => {
                                const key = `${eId}_${unitNum}`;
                                return (
                                  <Input
                                    key={key}
                                    value={unitNames[key] || ''}
                                    onChange={(e) => setUnitNames(prev => ({ ...prev, [key]: e.target.value }))}
                                    placeholder={`${eName} ${unitNum}`}
                                    className="h-8 text-sm"
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  onClick={handleAssignTeam}
                  disabled={selectedTeamIds.length === 0 || !installationScheduledDate || schedulingInstallation}
                >
                  {schedulingInstallation && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {schedulingInstallation ? 'Scheduling...' : 'Schedule Installation'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Project Detail Dialog */}
        <ProjectDetailModal
          open={projectDetailOpen}
          onOpenChange={setProjectDetailOpen}
          project={selectedProjectDetail}
          getStatusColor={getStatusColor}
        >
          {/* Admin-specific action buttons */}
          {selectedProjectDetail?.status === StageName.QuotePending && !selectedProjectDetail?.supervisorId && (
            <Button
              className="w-full bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                setProjectDetailOpen(false);
                setSelectedProjectForAssign(selectedProjectDetail);
                setAssignSupervisorDialogOpen(true);
              }}
            >
              Assign SuperAligner
            </Button>
          )}
          {selectedProjectDetail?.status === 'SV1 Complete' && !selectedProjectDetail?.siteVisit2Date && (
            <Button
              className="w-full bg-sky-600 hover:bg-sky-700"
              onClick={() => {
                setProjectDetailOpen(false);
                openProposeSv2(selectedProjectDetail);
              }}
            >
              Propose Site Visit 2
            </Button>
          )}
        </ProjectDetailModal>

        {/* Quotation Preparation Modal */}
        <QuotationPreparationModal
          open={quotationModalOpen}
          onOpenChange={setQuotationModalOpen}
          project={selectedProjectForQuotation}
          userRole="admin"
          existingQuotation={selectedProjectForQuotation?.draftQuotation || null}
          onQuotationCreated={() => {
            // Refresh projects list to show updated button
            if (activeTab === 'projects') {
              fetchProjectsData();
            }
          }}
        />

        {/* Edit Team Dialog */}
        <Dialog open={editTeamDialogOpen} onOpenChange={setEditTeamDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Team - {editTeamData.name}</DialogTitle>
              <DialogDescription>Manage team details and members</DialogDescription>
            </DialogHeader>
            <Tabs value={editTeamTab} onValueChange={setEditTeamTab} className="mt-4">
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                <TabsTrigger value="members" className="flex-1">Members ({editTeamMembers.length})</TabsTrigger>
                <TabsTrigger value="add" className="flex-1">+ Add Member</TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details">
                <form onSubmit={handleUpdateTeam} className="space-y-4 mt-4 pb-2">
                  <div className="space-y-2">
                    <Label>Team Name</Label>
                    <Input
                      value={editTeamData.name || ''}
                      onChange={(e) => setEditTeamData({ ...editTeamData, name: e.target.value })}
                      placeholder="Enter team name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Skill Level</Label>
                    <Select value={editTeamData.skillLevel} onValueChange={(v) => setEditTeamData({ ...editTeamData, skillLevel: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {skillLevels.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Select value={editTeamData.location} onValueChange={(v) => setEditTeamData({ ...editTeamData, location: v })}>
                      <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent>
                        {cities.map(city => (
                          <SelectItem key={city.id} value={city.cityName}>
                            {city.cityName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700">
                    Update Team
                  </Button>
                </form>
              </TabsContent>

              {/* Members Tab */}
              <TabsContent value="members">
                <div className="space-y-3 mt-4">
                  {editTeamMembers.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No members in this team</p>
                  ) : (
                    editTeamMembers.map((member) => (
                      <div key={member.userId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{member.userName}</h4>
                            {!member.isActive && (
                              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{member.email}</p>
                          <div className="flex gap-4 mt-1 text-xs text-gray-500">
                            {member.phoneNumber && <span>📞 {member.phoneNumber}</span>}
                            {member.role && <span>👤 {member.role}</span>}
                            <span>📅 Joined: {new Date(member.joinedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {member.isActive && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              try {
                                await api.removeTeamMember(editTeamData.id, member.userId);
                                toast({ title: 'Member Removed', description: `${member.userName} has been removed` });
                                const members = await api.getTeamMembersForTeam(editTeamData.id);
                                setEditTeamMembers(members);
                                const teamsData = await api.getTeams();
                                const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
                                setTeams(teamsList);
                              } catch (error) {
                                toast({ title: 'Error', description: error.message || 'Failed to remove member', variant: 'destructive' });
                              }
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Add Member Tab */}
              <TabsContent value="add">
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Select User</Label>
                    <Select value={editTeamNewMemberId} onValueChange={setEditTeamNewMemberId}>
                      <SelectTrigger><SelectValue placeholder="Select user to add" /></SelectTrigger>
                      <SelectContent>
                        {editTeamAvailableUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {editTeamAvailableUsers.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">No available users to add</p>
                  )}
                  <Button
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={!editTeamNewMemberId}
                    onClick={async () => {
                      try {
                        const response = await api.addTeamMember(editTeamData.id, editTeamNewMemberId);
                        toast({
                          title: 'Member Added',
                          description: response.isActive
                            ? `${response.userName} has been added to the team`
                            : `${response.userName} has been reactivated in the team`
                        });
                        setEditTeamNewMemberId('');
                        // Refresh members and available users
                        const [members, users] = await Promise.all([
                          api.getTeamMembersForTeam(editTeamData.id),
                          api.getAvailableUsersForTeams()
                        ]);
                        setEditTeamMembers(members);
                        setEditTeamAvailableUsers(users);
                        const teamsData = await api.getTeams();
                        const teamsList = Array.isArray(teamsData) ? teamsData : (teamsData.teams || teamsData.data || []);
                        setTeams(teamsList);
                      } catch (error) {
                        toast({ title: 'Error', description: error.message || 'Failed to add member', variant: 'destructive' });
                      }
                    }}
                  >
                    Add Member
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialogOpen} onOpenChange={setDeleteConfirmDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete team <strong>"{teamToDelete?.name}"</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 my-4">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This action will:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-800 mt-2 space-y-1">
                <li>Soft delete the team (mark as inactive)</li>
                <li>Remove all team members from this team</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setDeleteConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDeleteTeam}>
                Delete Team
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Generate Payment Link Dialog */}
        <Dialog open={paymentLinkDialogOpen} onOpenChange={setPaymentLinkDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Generate Payment Link</DialogTitle>
              <DialogDescription>
                Create a Razorpay payment link for {paymentLinkProject?.clientName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="dash-link-amount">Amount (₹) *</Label>
                <Input
                  id="dash-link-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="Enter amount"
                  value={paymentLinkForm.amount}
                  onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dash-link-desc">Description (optional)</Label>
                <Input
                  id="dash-link-desc"
                  placeholder="e.g., First installment payment"
                  value={paymentLinkForm.description}
                  onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dash-link-expiry">Link Expiry (hours)</Label>
                <Input
                  id="dash-link-expiry"
                  type="number"
                  min="1"
                  max="720"
                  value={paymentLinkForm.expiryHours}
                  onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, expiryHours: parseInt(e.target.value) || 72 })}
                />
                <p className="text-xs text-gray-500">Default: 72 hours (3 days)</p>
              </div>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={handleGeneratePaymentLink}
                disabled={generatingPaymentLink}
              >
                {generatingPaymentLink && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Generate Link
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Schedule SV1 Confirmation Dialog */}
        <AlertDialog open={scheduleSv1DialogOpen} onOpenChange={setScheduleSv1DialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Schedule SV1</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  {scheduleSv1Loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-amber-600 mr-2" />
                      <span>Loading SV1 booking details...</span>
                    </div>
                  ) : scheduleSv1Data ? (
                    <div>
                      <p className="mb-3">
                        This will move <strong>{scheduleSv1Project?.clientName}</strong> from "Quotation Approved" to "SV1 Scheduled".
                      </p>
                      <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                        <p><span className="text-gray-500">Date:</span> <span className="font-medium">{new Date(scheduleSv1Data.scheduledDate).toLocaleDateString()}</span></p>
                        <p><span className="text-gray-500">Slot:</span> <span className="font-medium">{scheduleSv1Data.slotName || 'N/A'}</span></p>
                        <p><span className="text-gray-500">SuperAligner:</span> <span className="font-medium">{scheduleSv1Data.supervisorName || 'N/A'}</span></p>
                        <p><span className="text-gray-500">Status:</span> <span className="font-medium capitalize">{scheduleSv1Data.status || 'N/A'}</span></p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-red-600">No SV1 booking found for this project. Please book SV1 from the project page first.</p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={scheduleSv1Submitting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-amber-600 hover:bg-amber-700"
                onClick={(e) => {
                  e.preventDefault();
                  handleScheduleSv1Confirm();
                }}
                disabled={scheduleSv1Loading || !scheduleSv1Data || scheduleSv1Data.status !== 'confirmed' || scheduleSv1Submitting}
              >
                {scheduleSv1Submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm Schedule
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Schedule SV2 Confirmation Dialog */}
        <AlertDialog open={scheduleSv2ConfirmOpen} onOpenChange={setScheduleSv2ConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Schedule SV2</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  {scheduleSv2Loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-amber-600 mr-2" />
                      <span>Loading SV2 booking details...</span>
                    </div>
                  ) : scheduleSv2Data ? (
                    <div>
                      <p className="mb-3">
                        This will move <strong>{scheduleSv2Project?.clientName}</strong> from "SV2 Proposed" to "SV2 Scheduled".
                      </p>
                      <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                        <p><span className="text-gray-500">Date:</span> <span className="font-medium">{new Date(scheduleSv2Data.scheduledDate).toLocaleDateString()}</span></p>
                        <p><span className="text-gray-500">Slot:</span> <span className="font-medium">{scheduleSv2Data.slotName || 'N/A'}</span></p>
                        <p><span className="text-gray-500">SuperAligner:</span> <span className="font-medium">{scheduleSv2Data.supervisorName || 'N/A'}</span></p>
                        <p><span className="text-gray-500">Status:</span> <span className="font-medium capitalize">{scheduleSv2Data.status || 'N/A'}</span></p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-red-600">No SV2 booking found for this project. Please propose SV2 first.</p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={scheduleSv2Submitting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleScheduleSv2Confirm}
                disabled={scheduleSv2Loading || !scheduleSv2Data || scheduleSv2Submitting}
              >
                {scheduleSv2Submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm Schedule
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* SV Scheduled Popup */}
        <Dialog open={svScheduledDialogOpen} onOpenChange={setSvScheduledDialogOpen}>
          <DialogContent className="max-w-lg max-h-[85dvh] sm:max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{svScheduledType} Scheduled</DialogTitle>
              <DialogDescription>Projects with {svScheduledType} scheduled</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {svScheduledProjects.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No scheduled visits found</p>
              ) : (
                svScheduledProjects.map((p, idx) => (
                  <div key={p.id || idx} className="p-3 border rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{p.clientName}</p>
                      <p className="text-xs text-gray-500">{p.location}</p>
                      {p.supervisorName && <p className="text-xs text-blue-600">Supervisor: {p.supervisorName}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-teal-700">
                        {p.scheduledDate ? formatDate(p.scheduledDate) : 'Date TBD'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Project Closure Dialog */}
        <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Close Project
              </DialogTitle>
              <DialogDescription>
                Review sign-off details and close {closureProject?.clientName}
              </DialogDescription>
            </DialogHeader>

            {closureLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : closureDetails ? (
              <div className="space-y-6 mt-4">
                {/* Project Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900">{closureDetails.clientName}</h4>
                  <p className="text-sm text-gray-500">{closureDetails.siteAddress} • {closureDetails.sqft} sq.ft</p>
                  {closureDetails.oemName && (
                    <p className="text-sm text-gray-500">OEM: {closureDetails.oemName}</p>
                  )}
                  {closureDetails.supervisorName && (
                    <p className="text-sm text-gray-500">Supervisor: {closureDetails.supervisorName}</p>
                  )}
                </div>

                {/* OEM Approval Info */}
                {closureDetails.oemApproval && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">OEM Approval</h4>
                    <p className="text-sm text-green-700">Approved by: {closureDetails.oemApproval.approvedBy}</p>
                    <p className="text-sm text-green-700">
                      Date: {closureDetails.oemApproval.approvedAt ? formatDate(closureDetails.oemApproval.approvedAt) : 'N/A'}
                    </p>
                    {closureDetails.oemApproval.notes && (
                      <p className="text-sm text-green-700 mt-1">{closureDetails.oemApproval.notes}</p>
                    )}
                  </div>
                )}

                {/* Rating */}
                {closureDetails.rating && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">OEM Rating:</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${closureDetails.rating >= star
                            ? 'text-yellow-500 fill-yellow-500'
                            : 'text-gray-300'
                            }`}
                        />
                      ))}
                      <span className="ml-1 text-sm font-medium text-gray-600">{closureDetails.rating}/5</span>
                    </div>
                  </div>
                )}

                {/* Client Review Summary */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Client Review Summary</h4>
                  <div className="space-y-4">
                    {closureDetails.groups?.map((group, gi) => (
                      <div key={gi}>
                        <p className="text-sm font-medium text-gray-700 mb-2">{group.groupName}</p>
                        <div className="space-y-2">
                          {group.items?.map((item) => (
                            <div key={item.id} className="p-3 bg-white border rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {item.isApproved ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  )}
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{item.itemName}</span>
                                    {item.isRequired && <span className="ml-1 text-xs text-red-500">*</span>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  {item.photoCount > 0 && (
                                    <button
                                      className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                                      onClick={() => loadClosureItemPhotos(closureProject.id, item.id)}
                                    >
                                      📷 {closurePhotosExpanded[item.id] ? 'Hide' : 'View'} Photos ({item.photoCount})
                                    </button>
                                  )}
                                  {item.approvedBy && (
                                    <span className="text-green-600">✓ {item.approvedBy}</span>
                                  )}
                                </div>
                              </div>
                              {item.notes && (
                                <p className="text-xs text-gray-500 mt-1 ml-6">
                                  <span className="font-medium">Notes:</span> {item.notes}
                                </p>
                              )}
                              {/* Photo thumbnails */}
                              {closurePhotosExpanded[item.id] && closureItemPhotos[item.id] && (
                                <div className="mt-2 ml-6 flex flex-wrap gap-2">
                                  {closureItemPhotos[item.id].map((photo) => (
                                    <img
                                      key={photo.id}
                                      src={photo.url}
                                      alt={photo.fileName}
                                      className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => setClosureEnlargedPhoto(photo)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-sm text-gray-600">
                    {closureDetails.completedItems}/{closureDetails.totalItems} items approved
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Admin Notes (optional)</h4>
                  <textarea
                    value={closureNotes}
                    onChange={(e) => setClosureNotes(e.target.value)}
                    placeholder="Add any final notes..."
                    className="w-full p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows={3}
                  />
                </div>

                {/* Close Button */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setClosureDialogOpen(false)}
                    disabled={closureSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={handleCloseProject}
                    disabled={closureSubmitting}
                  >
                    {closureSubmitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Closing...</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" /> Close Project</>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Failed to load details</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Enlarged Photo Dialog */}
        {closureEnlargedPhoto && (
          <Dialog open={!!closureEnlargedPhoto} onOpenChange={() => setClosureEnlargedPhoto(null)}>
            <DialogContent className="max-w-[95vw] sm:max-w-3xl p-2">
              <DialogHeader>
                <DialogTitle className="text-sm">{closureEnlargedPhoto.fileName}</DialogTitle>
              </DialogHeader>
              <img
                src={closureEnlargedPhoto.url}
                alt={closureEnlargedPhoto.fileName}
                className="w-full max-h-[75vh] object-contain rounded"
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div >
  );
};

export default EnhancedAdminDashboard;
